import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import { authService } from '../lib/auth';
import { toast } from 'sonner';
import { 
  Plus, Search, Calendar, Clock, User, CheckCircle, XCircle,
  UserCheck, Edit2, Trash2, Loader2, X, PlusCircle, FileText, BookOpen, Copy,
  ChevronLeft, ChevronRight, CalendarDays, CheckSquare, Square, Settings, Youtube,
  ExternalLink, Image, Upload
} from 'lucide-react';

const Classes = ({ classType = 'app' }) => {
  // Helper: get local YYYY-MM-DD from a date string (avoids UTC timezone shift)
  const toLocalDateStr = (dateVal) => {
    const d = new Date(dateVal);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  const [searchParams, setSearchParams] = useSearchParams();
  const [classes, setClasses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [courses, setCourses] = useState([]);
  const [youtubeChannels, setYoutubeChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showSubstituteModal, setShowSubstituteModal] = useState(false);
  const [showAddSubjectModal, setShowAddSubjectModal] = useState(false);
  const [showAddCourseModal, setShowAddCourseModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showChannelModal, setShowChannelModal] = useState(false);
  const [selectedClass, setSelectedClass] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [newSubject, setNewSubject] = useState('');
  const [newCourse, setNewCourse] = useState('');
  const [courseSearch, setCourseSearch] = useState('');
  const [newChannelName, setNewChannelName] = useState('');
  const [editingChannel, setEditingChannel] = useState(null);
  const [materialFilter, setMaterialFilter] = useState('all'); // 'all', 'pdf', 'dpp', 'no_pdf', 'no_dpp'
  
  // Multi-select state
  const [selectedClasses, setSelectedClasses] = useState([]);
  const [deletingMultiple, setDeletingMultiple] = useState(false);
  
  // Date filtering state
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [importDate, setImportDate] = useState('');
  const [importingClasses, setImportingClasses] = useState(false);
  
  // Determine if this is YouTube classes
  const isYouTube = classType === 'youtube';
  const pageTitle = isYouTube ? 'YouTube Classes' : 'App Classes';
  
  const [formData, setFormData] = useState({
    title: '',
    subject: '',
    topic: '',
    courses: [],
    teacher_id: '',
    scheduled_date: '',
    duration_minutes: 60,
    pdf_attached: false,
    dpp_attached: false,
    dpp_required: true,
    youtube_channel_id: '',
    event_link: ''
  });
  const [statusData, setStatusData] = useState({
    status: '',
    actual_duration_minutes: '',
    cancellation_reason: ''
  });
  const [substituteId, setSubstituteId] = useState('');
  const user = authService.getCurrentUser();

  // Read URL parameters on mount
  useEffect(() => {
    const dateParam = searchParams.get('date');
    const statusParam = searchParams.get('status');
    const materialParam = searchParams.get('material');
    
    if (dateParam) {
      setSelectedDate(dateParam);
    }
    if (statusParam) {
      setStatusFilter(statusParam);
    }
    if (materialParam) {
      setMaterialFilter(materialParam);
    }
  }, [searchParams]);

  useEffect(() => {
    fetchClasses();
    fetchTeachers();
    fetchSubjects();
    fetchCourses();
    if (isYouTube) {
      fetchYouTubeChannels();
    }
  }, [classType]);

  // Re-fetch when date changes to get correct range from backend
  useEffect(() => {
    fetchClasses();
  }, [selectedDate]);

  const fetchClasses = async () => {
    try {
      // Send date range to backend so we get classes for the selected week, not just latest 500
      const selDate = new Date(selectedDate);
      const startDate = new Date(selDate);
      startDate.setDate(startDate.getDate() - 7);
      const endDate = new Date(selDate);
      endDate.setDate(endDate.getDate() + 7);
      const date_from = startDate.toISOString().split('T')[0] + 'T00:00:00';
      const date_to = endDate.toISOString().split('T')[0] + 'T23:59:59';
      const response = await api.getClasses({ class_type: classType, date_from, date_to });
      setClasses(response.data);
    } catch (error) {
      toast.error('Failed to fetch classes');
    } finally {
      setLoading(false);
    }
  };

  const fetchYouTubeChannels = async () => {
    try {
      const response = await api.getYouTubeChannels();
      setYoutubeChannels(response.data || []);
    } catch (error) {
      console.error('Failed to fetch YouTube channels');
    }
  };

  // Helper to normalize and match status
  const matchesStatus = (classStatus, filterStatus) => {
    if (filterStatus === 'all') return true;
    const normalized = classStatus?.toLowerCase() || '';
    const filterNorm = filterStatus.toLowerCase();
    
    const statusMap = {
      'run as per plan': ['run as per plan', 'run_as_per_plan', 'completed'],
      'proxy teacher used': ['proxy teacher used', 'proxy_teacher_used', 'substitute_arranged'],
      'scheduled': ['scheduled'],
      'cancelled': ['cancelled']
    };
    
    const matchValues = statusMap[filterNorm] || [filterNorm];
    return matchValues.includes(normalized);
  };

  const fetchTeachers = async () => {
    try {
      const response = await api.getTeachers();
      setTeachers(response.data);
    } catch (error) {
      console.error('Failed to fetch teachers');
    }
  };

  const fetchSubjects = async () => {
    try {
      const response = await api.getSubjects();
      setSubjects(response.data);
    } catch (error) {
      console.error('Failed to fetch subjects');
    }
  };

  const fetchCourses = async () => {
    try {
      const response = await api.getCoursesList();
      setCourses(response.data);
    } catch (error) {
      console.error('Failed to fetch courses');
    }
  };

  const handleAddSubject = async (e) => {
    e.preventDefault();
    if (!newSubject.trim()) return;
    try {
      await api.createSubject({ name: newSubject.trim() });
      toast.success('Subject added successfully');
      setNewSubject('');
      setShowAddSubjectModal(false);
      fetchSubjects();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add subject');
    }
  };

  const handleAddCourse = async (e) => {
    e.preventDefault();
    if (!newCourse.trim()) return;
    try {
      await api.createCourse({ name: newCourse.trim() });
      toast.success('Course added successfully');
      setNewCourse('');
      setShowAddCourseModal(false);
      fetchCourses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add course');
    }
  };

  const handleAddChannel = async (e) => {
    e.preventDefault();
    if (!newChannelName.trim()) return;
    try {
      if (editingChannel) {
        await api.updateYouTubeChannel(editingChannel.id, { name: newChannelName.trim() });
        toast.success('Channel updated successfully');
      } else {
        await api.createYouTubeChannel({ name: newChannelName.trim() });
        toast.success('Channel added successfully');
      }
      setNewChannelName('');
      setEditingChannel(null);
      fetchYouTubeChannels();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save channel');
    }
  };

  const handleDeleteChannel = async (channel) => {
    if (!window.confirm(`Delete channel "${channel.name}"?`)) return;
    try {
      await api.deleteYouTubeChannel(channel.id);
      toast.success('Channel deleted');
      fetchYouTubeChannels();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete channel');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        courses: formData.courses.length > 0 ? formData.courses : []
      };
      
      if (selectedClass) {
        // For UPDATE: Don't send scheduled_date if it hasn't changed
        // This prevents time from being modified
        const originalDate = new Date(selectedClass.scheduled_date).toISOString().slice(0, 16);
        const newDate = formData.scheduled_date;
        
        if (originalDate === newDate) {
          // Remove scheduled_date from update to preserve original time
          delete submitData.scheduled_date;
        }
        
        await api.updateClass(selectedClass.id, submitData, classType);
        toast.success('Class updated successfully');
      } else {
        await api.createClass({ ...submitData, class_type: classType });
        toast.success('Class created successfully');
      }
      setShowModal(false);
      resetForm();
      fetchClasses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save class');
    }
  };

  const handleStatusUpdate = async (e) => {
    e.preventDefault();
    try {
      await api.updateClassStatus(selectedClass.id, {
        status: statusData.status,
        actual_duration_minutes: statusData.actual_duration_minutes ? parseInt(statusData.actual_duration_minutes) : null,
        cancellation_reason: statusData.cancellation_reason || null
      }, classType);
      toast.success('Event status updated');
      setShowStatusModal(false);
      fetchClasses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleToggleAttribute = async (classId, attribute) => {
    try {
      const res = await api.toggleClassAttribute(classId, attribute);
      const attrName = attribute === 'pdf_attached' ? 'PDF' : attribute === 'dpp_attached' ? 'DPP' : 'Event';
      toast.success(`${attrName} status updated`);
      fetchClasses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleSubstituteAssign = async (e) => {
    e.preventDefault();
    try {
      await api.assignSubstitute(selectedClass.id, { substitute_id: substituteId });
      toast.success('Proxy teacher assigned successfully');
      setShowSubstituteModal(false);
      setSubstituteId('');
      fetchClasses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign proxy teacher');
    }
  };

  const handleDelete = async (classId) => {
    if (!window.confirm('Are you sure you want to delete this class?')) return;
    try {
      await api.deleteClass(classId, classType);
      toast.success('Class deleted successfully');
      fetchClasses();
    } catch (error) {
      toast.error('Failed to delete class');
    }
  };

  // Multi-select functions
  const toggleClassSelection = (classId) => {
    setSelectedClasses(prev => 
      prev.includes(classId) 
        ? prev.filter(id => id !== classId)
        : [...prev, classId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedClasses.length === displayedClasses.length) {
      setSelectedClasses([]);
    } else {
      setSelectedClasses(displayedClasses.map(cls => cls.id));
    }
  };

  const handleDeleteMultiple = async () => {
    if (selectedClasses.length === 0) return;
    
    if (!window.confirm(`Are you sure you want to delete ${selectedClasses.length} classes? This action cannot be undone.`)) {
      return;
    }
    
    setDeletingMultiple(true);
    try {
      await api.deleteMultipleClasses(selectedClasses, classType);
      toast.success(`${selectedClasses.length} classes deleted successfully`);
      setSelectedClasses([]);
      fetchClasses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete classes');
    } finally {
      setDeletingMultiple(false);
    }
  };

  const clearSelection = () => {
    setSelectedClasses([]);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      subject: '',
      topic: '',
      courses: [],
      teacher_id: '',
      scheduled_date: '',
      duration_minutes: 60,
      pdf_attached: false,
      dpp_attached: false,
      dpp_required: true,
      youtube_channel_id: ''
    });
    setSelectedClass(null);
    setCourseSearch('');
  };

  // Copy from last class with same teacher and course
  const copyFromLastClass = async () => {
    if (!formData.teacher_id) {
      toast.error('Please select a teacher first');
      return;
    }
    
    // For App classes, also require course selection
    if (!isYouTube && formData.courses.length === 0) {
      toast.error('Please select at least one course first');
      return;
    }
    
    let matchingClasses;
    if (isYouTube) {
      // For YouTube, just match by teacher
      matchingClasses = classes.filter(cls => 
        cls.teacher_id === formData.teacher_id
      ).sort((a, b) => new Date(b.scheduled_date) - new Date(a.scheduled_date));
    } else {
      // For App classes, match by teacher and course
      matchingClasses = classes.filter(cls => 
        cls.teacher_id === formData.teacher_id && 
        cls.courses?.some(c => formData.courses.includes(c))
      ).sort((a, b) => new Date(b.scheduled_date) - new Date(a.scheduled_date));
    }
    
    if (matchingClasses.length === 0) {
      toast.error(isYouTube ? 'No previous class found for this teacher' : 'No previous class found for this teacher and course combination');
      return;
    }
    
    const lastClass = matchingClasses[0];
    
    setFormData(prev => ({
      ...prev,
      title: lastClass.title,
      subject: lastClass.subject,
      topic: lastClass.topic || '',
      duration_minutes: lastClass.duration_minutes,
      pdf_attached: false,
      dpp_attached: false
    }));
    
    toast.success(`Copied from: ${lastClass.title} (${new Date(lastClass.scheduled_date).toLocaleDateString()})`);
  };

  // Import all classes from a previous date to current selected date
  const handleImportFromDate = async () => {
    if (!importDate) {
      toast.error('Please select a date to import from');
      return;
    }
    
    const sourceClasses = classes.filter(cls => {
      const classDate = toLocalDateStr(cls.scheduled_date);
      return classDate === importDate;
    });
    
    if (sourceClasses.length === 0) {
      toast.error(`No classes found for ${new Date(importDate).toLocaleDateString()}`);
      return;
    }
    
    setImportingClasses(true);
    let successCount = 0;
    let errorCount = 0;
    
    for (const cls of sourceClasses) {
      try {
        // Calculate the new date by keeping the same time but changing the date
        const originalDate = new Date(cls.scheduled_date);
        const newDate = new Date(selectedDate);
        newDate.setHours(originalDate.getHours(), originalDate.getMinutes(), 0, 0);
        
        const newClassData = {
          title: cls.title,
          subject: cls.subject,
          topic: cls.topic || '',
          courses: cls.courses || [],
          teacher_id: cls.teacher_id,
          scheduled_date: newDate.toISOString(),
          duration_minutes: cls.duration_minutes,
          pdf_attached: false,
          dpp_attached: false,
          dpp_required: cls.dpp_required !== false,  // preserve "Not Required" flag from the original class
          youtube_channel_id: cls.youtube_channel_id || ''
        };
        
        await api.createClass({ ...newClassData, class_type: classType });
        successCount++;
      } catch (error) {
        errorCount++;
        console.error('Failed to import class:', cls.title, error);
      }
    }
    
    setImportingClasses(false);
    setShowImportModal(false);
    setImportDate('');
    
    if (successCount > 0) {
      toast.success(`Successfully imported ${successCount} classes to ${new Date(selectedDate).toLocaleDateString()}`);
      fetchClasses();
    }
    if (errorCount > 0) {
      toast.error(`Failed to import ${errorCount} classes`);
    }
  };

  // Get yesterday's date
  const getYesterdayDate = () => {
    const yesterday = new Date(selectedDate);
    yesterday.setDate(yesterday.getDate() - 1);
    return yesterday.toISOString().split('T')[0];
  };

  // Quick import from yesterday
  const handleImportFromYesterday = () => {
    setImportDate(getYesterdayDate());
    setShowImportModal(true);
  };

  const openEditModal = (cls) => {
    setSelectedClass(cls);
    setFormData({
      title: cls.title,
      subject: cls.subject,
      topic: cls.topic || '',
      courses: cls.courses || (cls.course ? [cls.course] : []),
      teacher_id: cls.teacher_id,
      scheduled_date: cls.scheduled_date ? new Date(cls.scheduled_date).toISOString().slice(0, 16) : '',
      duration_minutes: cls.duration_minutes,
      pdf_attached: cls.pdf_attached || false,
      dpp_attached: cls.dpp_attached || false,
      dpp_required: cls.dpp_required !== false,  // default to required for legacy rows
      youtube_channel_id: cls.youtube_channel_id || '',
      event_link: cls.event_link || ''
    });
    setShowModal(true);
  };

  const openStatusModal = (cls) => {
    setSelectedClass(cls);
    setStatusData({
      status: cls.status,
      actual_duration_minutes: cls.actual_duration_minutes || '',
      cancellation_reason: cls.cancellation_reason || ''
    });
    setShowStatusModal(true);
  };

  const openSubstituteModal = (cls) => {
    setSelectedClass(cls);
    setSubstituteId('');
    setShowSubstituteModal(true);
  };

  const handleCourseToggle = (courseName) => {
    setFormData(prev => {
      const newCourses = prev.courses.includes(courseName)
        ? prev.courses.filter(c => c !== courseName)
        : [...prev.courses, courseName];
      return { ...prev, courses: newCourses };
    });
  };

  // Date navigation
  const goToPreviousDay = () => {
    const date = new Date(selectedDate);
    date.setDate(date.getDate() - 1);
    setSelectedDate(date.toISOString().split('T')[0]);
  };

  const goToNextDay = () => {
    const date = new Date(selectedDate);
    date.setDate(date.getDate() + 1);
    setSelectedDate(date.toISOString().split('T')[0]);
  };

  const goToToday = () => {
    setSelectedDate(new Date().toISOString().split('T')[0]);
  };

  const getStatusBadge = (status) => {
    const normalizedStatus = status?.toLowerCase() || '';
    
    const styles = {
      'scheduled': 'bg-blue-100 text-blue-700',
      'run as per plan': 'bg-emerald-100 text-emerald-700',
      'run_as_per_plan': 'bg-emerald-100 text-emerald-700',
      'completed': 'bg-emerald-100 text-emerald-700',
      'cancelled': 'bg-red-100 text-red-700',
      'proxy teacher used': 'bg-amber-100 text-amber-700',
      'proxy_teacher_used': 'bg-amber-100 text-amber-700',
      'substitute_arranged': 'bg-amber-100 text-amber-700',
      'old recording': 'bg-slate-100 text-slate-700',
      'old_recording': 'bg-slate-100 text-slate-700',
      'new recording': 'bg-purple-100 text-purple-700',
      'new_recording': 'bg-purple-100 text-purple-700'
    };
    const labels = {
      'scheduled': 'Scheduled',
      'run as per plan': 'Run as per Plan',
      'run_as_per_plan': 'Run as per Plan',
      'completed': 'Run as per Plan',
      'cancelled': 'Cancelled',
      'proxy teacher used': 'Proxy Teacher Used',
      'proxy_teacher_used': 'Proxy Teacher Used',
      'substitute_arranged': 'Proxy Teacher Used',
      'old recording': 'Old Recording',
      'old_recording': 'Old Recording',
      'new recording': 'New Recording',
      'new_recording': 'New Recording'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[normalizedStatus] || 'bg-slate-100 text-slate-700'}`}>
        {labels[normalizedStatus] || status}
      </span>
    );
  };

  const getCoursesDisplay = (cls) => {
    const coursesList = cls.courses || (cls.course ? [cls.course] : []);
    if (coursesList.length === 0) return 'N/A';
    if (coursesList.length === 1) return coursesList[0];
    return `${coursesList[0]} +${coursesList.length - 1} more`;
  };

  // Combined filter: date + status + material + search term
  const displayedClasses = classes.filter(cls => {
    // First check date filter - use local date to avoid timezone shift
    const d = new Date(cls.scheduled_date);
    const classDate = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    if (classDate !== selectedDate) return false;
    
    // Then check status filter
    if (!matchesStatus(cls.status, statusFilter)) return false;
    
    // Check material filter
    if (materialFilter !== 'all') {
      if (materialFilter === 'pdf' && !cls.pdf_attached) return false;
      if (materialFilter === 'dpp' && !cls.dpp_attached) return false;
      if (materialFilter === 'no_pdf' && cls.pdf_attached) return false;
      // "Missing DPP" should ignore classes where DPP is marked Not Required
      if (materialFilter === 'no_dpp' && (cls.dpp_attached || cls.dpp_required === false)) return false;
      if (materialFilter === 'dpp_not_required' && cls.dpp_required !== false) return false;
    }
    
    // Then check search term
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (
        cls.title?.toLowerCase().includes(search) ||
        cls.subject?.toLowerCase().includes(search) ||
        cls.topic?.toLowerCase().includes(search) ||
        cls.teacher_name?.toLowerCase().includes(search)
      );
    }
    return true;
  }).sort((a, b) => new Date(a.scheduled_date) - new Date(b.scheduled_date));

  const canManageClasses = ['admin', 'manager', 'class_conduction_agent'].includes(user?.role);
  const canAssignSubstitute = ['admin', 'manager'].includes(user?.role);

  // Check if selected date has any classes
  const selectedDateHasClasses = displayedClasses.length > 0;
  
  // Get available dates that have classes (for import)
  const datesWithClasses = [...new Set(classes.map(cls => 
    toLocalDateStr(cls.scheduled_date)
  ))].sort().reverse();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-indigo-600" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="classes-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{pageTitle}</h1>
          <p className="text-slate-600 mt-1">Manage class schedules and events</p>
        </div>
        <div className="flex items-center gap-2">
          {/* YouTube Channel Settings - Only for YouTube classes and managers/admins */}
          {isYouTube && (user?.role === 'admin' || user?.role === 'manager') && (
            <button
              onClick={() => setShowChannelModal(true)}
              className="flex items-center gap-2 px-3 py-2 border border-red-200 text-red-600 rounded-lg hover:bg-red-50 transition-all"
              data-testid="manage-channels-btn"
              title="Manage YouTube Channels"
            >
              <Settings size={18} />
              <span className="hidden sm:inline">Channels</span>
            </button>
          )}
          {canManageClasses && (
            <>
              <button
                onClick={handleImportFromYesterday}
                className="flex items-center gap-2 px-3 py-2 border border-indigo-200 text-indigo-600 rounded-lg hover:bg-indigo-50 transition-all"
                data-testid="import-yesterday-btn"
                title="Import all classes from yesterday to selected date"
              >
                <Copy size={18} />
                <span className="hidden sm:inline">Import from Yesterday</span>
              </button>
              <button
                onClick={() => { resetForm(); setShowModal(true); }}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-all"
                data-testid="add-class-btn"
              >
                <Plus size={20} />
                <span>Add Class</span>
              </button>
            </>
          )}
        </div>
      </div>

      {/* Date Navigation */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <button
              onClick={goToPreviousDay}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              data-testid="prev-day-btn"
            >
              <ChevronLeft size={20} />
            </button>
            
            <div className="flex items-center gap-2">
              <CalendarDays className="text-indigo-600" size={20} />
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 font-medium"
                data-testid="date-picker"
              />
            </div>
            
            <button
              onClick={goToNextDay}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              data-testid="next-day-btn"
            >
              <ChevronRight size={20} />
            </button>
            
            <button
              onClick={goToToday}
              className="px-3 py-1.5 text-sm bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
              data-testid="today-btn"
            >
              Today
            </button>
          </div>
          
          <div className="text-center sm:text-right">
            <p className="text-lg font-semibold text-slate-900">
              {new Date(selectedDate).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
            <p className="text-sm text-slate-500">
              {displayedClasses.length} class{displayedClasses.length !== 1 ? 'es' : ''} scheduled
            </p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-3 sm:gap-4">
        <div className="relative w-full">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
          <input
            type="text"
            placeholder="Search classes, topics..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
            data-testid="search-input"
          />
        </div>
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full sm:w-auto px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
            data-testid="status-filter"
          >
            <option value="all">All Event Status</option>
            <option value="Scheduled">Scheduled</option>
            <option value="Run as per Plan">Run as per Plan</option>
            <option value="Cancelled">Cancelled</option>
            <option value="Proxy Teacher Used">Proxy Teacher Used</option>
            <option value="Old Recording">Old Recording</option>
            <option value="New Recording">New Recording</option>
          </select>
          <select
            value={materialFilter}
            onChange={(e) => setMaterialFilter(e.target.value)}
            className="w-full sm:w-auto px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
            data-testid="material-filter"
          >
            <option value="all">All Materials</option>
            <option value="pdf">With PDF</option>
            <option value="dpp">With DPP</option>
            <option value="no_pdf">Missing PDF</option>
            <option value="no_dpp">Missing DPP</option>
            <option value="dpp_not_required">DPP Not Required</option>
          </select>
          {(statusFilter !== 'all' || materialFilter !== 'all') && (
            <button
              onClick={() => {
                setStatusFilter('all');
                setMaterialFilter('all');
                setSearchParams({});
              }}
              className="w-full sm:w-auto px-3 py-2 text-sm text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
              data-testid="clear-filters-btn"
            >
              Clear Filters
            </button>
          )}
        </div>
      </div>

      {/* No Classes Message with Import Option */}
      {!selectedDateHasClasses && canManageClasses && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 text-center">
          <Calendar className="mx-auto text-amber-500 mb-3" size={40} />
          <h3 className="text-lg font-semibold text-amber-800 mb-2">No classes scheduled for this date</h3>
          <p className="text-amber-700 mb-4">Would you like to import classes from a previous date?</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              onClick={handleImportFromYesterday}
              className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-all"
            >
              <Copy size={18} />
              Import from Yesterday
            </button>
            <button
              onClick={() => setShowImportModal(true)}
              className="flex items-center gap-2 px-4 py-2 border border-amber-500 text-amber-700 rounded-lg hover:bg-amber-100 transition-all"
            >
              <CalendarDays size={18} />
              Choose Another Date
            </button>
          </div>
        </div>
      )}

      {/* Bulk Action Bar */}
      {selectedClasses.length > 0 && (
        <div className="bg-indigo-600 text-white rounded-xl p-4 flex items-center justify-between shadow-lg">
          <div className="flex items-center gap-3">
            <CheckSquare size={20} />
            <span className="font-medium">{selectedClasses.length} class{selectedClasses.length > 1 ? 'es' : ''} selected</span>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={clearSelection}
              className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors text-sm font-medium"
            >
              Clear Selection
            </button>
            <button
              onClick={handleDeleteMultiple}
              disabled={deletingMultiple}
              className="px-4 py-2 bg-red-500 hover:bg-red-600 rounded-lg transition-colors text-sm font-medium flex items-center gap-2 disabled:opacity-50"
              data-testid="bulk-delete-btn"
            >
              {deletingMultiple ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 size={16} />
                  Delete Selected
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Classes Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                {canManageClasses && (
                  <th className="px-4 py-3 w-10">
                    <button
                      onClick={toggleSelectAll}
                      className="p-1 hover:bg-slate-200 rounded transition-colors"
                      title={selectedClasses.length === displayedClasses.length ? "Deselect all" : "Select all"}
                    >
                      {selectedClasses.length === displayedClasses.length && displayedClasses.length > 0 ? (
                        <CheckSquare size={18} className="text-indigo-600" />
                      ) : (
                        <Square size={18} className="text-slate-400" />
                      )}
                    </button>
                  </th>
                )}
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Time</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Class</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Teacher</th>
                {isYouTube && (
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Channel</th>
                )}
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Duration</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Materials</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Event Status</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Last Updated By</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {displayedClasses.length === 0 ? (
                <tr>
                  <td colSpan={canManageClasses ? (isYouTube ? 11 : 10) : (isYouTube ? 10 : 9)} className="px-6 py-12 text-center text-slate-500">
                    No classes found for selected filters
                  </td>
                </tr>
              ) : (
                displayedClasses.map((cls) => (
                  <tr key={cls.id} className={`hover:bg-slate-50 ${selectedClasses.includes(cls.id) ? 'bg-indigo-50' : ''}`} data-testid={`class-row-${cls.id}`}>
                    {canManageClasses && (
                      <td className="px-4 py-4">
                        <button
                          onClick={() => toggleClassSelection(cls.id)}
                          className="p-1 hover:bg-slate-200 rounded transition-colors"
                        >
                          {selectedClasses.includes(cls.id) ? (
                            <CheckSquare size={18} className="text-indigo-600" />
                          ) : (
                            <Square size={18} className="text-slate-400" />
                          )}
                        </button>
                      </td>
                    )}
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 text-slate-900 font-medium">
                        <Clock size={16} className="text-indigo-500" />
                        <span>{new Date(cls.scheduled_date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-medium text-slate-900">{cls.title}</p>
                        <p className="text-sm text-slate-500">
                          {cls.subject} {cls.topic && `• ${cls.topic}`}
                        </p>
                        <p className="text-xs text-indigo-600 mt-1" title={cls.courses?.join(', ') || cls.course}>
                          {getCoursesDisplay(cls)}
                        </p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <User size={16} className="text-slate-400" />
                        <span className="text-slate-900">{cls.teacher_name}</span>
                      </div>
                      {cls.substitute_name && (
                        <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                          <UserCheck size={12} />
                          Proxy: {cls.substitute_name}
                        </p>
                      )}
                    </td>
                    {isYouTube && (
                      <td className="px-6 py-4">
                        {cls.youtube_channel_name ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-50 text-red-700 text-sm rounded-full">
                            <Youtube size={14} />
                            {cls.youtube_channel_name}
                          </span>
                        ) : (
                          <span className="text-slate-400 text-sm">-</span>
                        )}
                      </td>
                    )}
                    <td className="px-6 py-4 text-slate-600">
                      {cls.duration_minutes} min
                      {cls.actual_duration_minutes && (
                        <span className="text-emerald-600 block text-sm">
                          Actual: {cls.actual_duration_minutes} min
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        <button
                          onClick={() => handleToggleAttribute(cls.id, 'pdf_attached')}
                          className={`flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium transition-colors ${
                            cls.pdf_attached 
                              ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200' 
                              : 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                          }`}
                          data-testid={`toggle-pdf-${cls.id}`}
                        >
                          <FileText size={12} />
                          PDF: {cls.pdf_attached ? '✓' : '✗'}
                        </button>
                        {cls.dpp_required === false ? (
                          <span
                            className="flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-500"
                            title="DPP marked Not Required for this class"
                            data-testid={`dpp-not-required-${cls.id}`}
                          >
                            <BookOpen size={12} />
                            DPP: N/R
                          </span>
                        ) : (
                          <button
                            onClick={() => handleToggleAttribute(cls.id, 'dpp_attached')}
                            className={`flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium transition-colors ${
                              cls.dpp_attached
                                ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                                : 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                            }`}
                            data-testid={`toggle-dpp-${cls.id}`}
                          >
                            <BookOpen size={12} />
                            DPP: {cls.dpp_attached ? '✓' : '✗'}
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        <button
                          onClick={() => handleToggleAttribute(cls.id, 'event_created')}
                          className={`flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium transition-colors ${
                            cls.event_created 
                              ? 'bg-blue-100 text-blue-700 hover:bg-blue-200' 
                              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                          }`}
                          data-testid={`toggle-event-${cls.id}`}
                        >
                          <Calendar size={12} />
                          Event: {cls.event_created ? '✓' : '✗'}
                        </button>
                        {getStatusBadge(cls.status)}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        {cls.last_updated_by_name ? (
                          <span className="text-xs text-slate-500">{cls.last_updated_by_name}</span>
                        ) : (
                          <span className="text-xs text-slate-300">-</span>
                        )}
                        {isYouTube && cls.event_link && (
                          <a href={cls.event_link} target="_blank" rel="noopener noreferrer" 
                            className="flex items-center gap-1 text-xs text-blue-600 hover:underline">
                            <ExternalLink size={10} /> Link
                          </a>
                        )}
                        {isYouTube && cls.thumbnail_url && (
                          <img src={cls.thumbnail_url} alt="" className="w-12 h-8 rounded object-cover mt-1" />
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        {canAssignSubstitute && cls.status === 'Scheduled' && (
                          <button
                            onClick={() => openSubstituteModal(cls)}
                            className="px-3 py-1.5 bg-amber-500 text-white rounded-lg hover:bg-amber-600 text-sm font-medium flex items-center gap-1"
                            title="Assign Proxy Teacher"
                            data-testid={`proxy-btn-${cls.id}`}
                          >
                            <UserCheck size={16} />
                            <span className="hidden sm:inline">Proxy</span>
                          </button>
                        )}
                        {canManageClasses && (
                          <>
                            <button
                              onClick={() => openStatusModal(cls)}
                              className="p-1.5 text-slate-600 hover:text-indigo-600 hover:bg-indigo-50 rounded"
                              title="Update Event Status"
                              data-testid={`status-btn-${cls.id}`}
                            >
                              <CheckCircle size={18} />
                            </button>
                            <button
                              onClick={() => openEditModal(cls)}
                              className="p-1.5 text-slate-600 hover:text-amber-600 hover:bg-amber-50 rounded"
                              title="Edit"
                              data-testid={`edit-btn-${cls.id}`}
                            >
                              <Edit2 size={18} />
                            </button>
                          </>
                        )}
                        {['admin', 'manager'].includes(user?.role) && (
                          <button
                            onClick={() => handleDelete(cls.id)}
                            className="p-1.5 text-slate-600 hover:text-red-600 hover:bg-red-50 rounded"
                            title="Delete"
                            data-testid={`delete-btn-${cls.id}`}
                          >
                            <Trash2 size={18} />
                          </button>
                        )}
                        {isYouTube && canManageClasses && (
                          <label
                            className="p-1.5 text-slate-600 hover:text-purple-600 hover:bg-purple-50 rounded cursor-pointer"
                            title="Upload Thumbnail"
                          >
                            <Image size={18} />
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={async (e) => {
                                const file = e.target.files[0];
                                if (!file) return;
                                try {
                                  const fd = new FormData();
                                  fd.append('thumbnail', file);
                                  await api.uploadClassThumbnail(cls.id, fd, classType);
                                  toast.success('Thumbnail uploaded');
                                  fetchClasses();
                                } catch (err) {
                                  toast.error(err.response?.data?.detail || 'Upload failed');
                                }
                                e.target.value = '';
                              }}
                            />
                          </label>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-slate-900">
                {selectedClass ? 'Update Class' : 'Create New Class'}
              </h2>
              <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-600">
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Class Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  required
                  data-testid="class-title-input"
                />
              </div>
              
              {/* Subject */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Subject *</label>
                <div className="flex gap-2">
                  <select
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    className="flex-1 px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    required
                    data-testid="class-subject-select"
                  >
                    <option value="">Select Subject</option>
                    {subjects.map((s) => (
                      <option key={s.id} value={s.name}>{s.name}</option>
                    ))}
                  </select>
                  {['admin', 'manager'].includes(user?.role) && (
                    <button
                      type="button"
                      onClick={() => setShowAddSubjectModal(true)}
                      className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg"
                      title="Add Subject"
                    >
                      <PlusCircle size={20} />
                    </button>
                  )}
                </div>
              </div>

              {/* Topic */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Topic</label>
                <input
                  type="text"
                  value={formData.topic}
                  onChange={(e) => setFormData({ ...formData, topic: e.target.value })}
                  placeholder="e.g., Introduction to Algebra"
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  data-testid="class-topic-input"
                />
              </div>

              {/* Courses - Not required for YouTube classes */}
              {!isYouTube && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Courses * <span className="text-xs text-slate-500">(Select one or more)</span>
                </label>
                <div className="flex gap-2 mb-2">
                  {['admin', 'manager'].includes(user?.role) && (
                    <button
                      type="button"
                      onClick={() => setShowAddCourseModal(true)}
                      className="text-sm text-indigo-600 hover:text-indigo-700 flex items-center gap-1"
                    >
                      <PlusCircle size={16} />
                      Add New Course
                    </button>
                  )}
                </div>
                <div className="relative mb-2">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                  <input
                    type="text"
                    value={courseSearch}
                    onChange={(e) => setCourseSearch(e.target.value)}
                    placeholder={`Search ${courses.length} courses…`}
                    data-testid="course-search-input"
                    className="w-full pl-9 pr-8 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  {courseSearch && (
                    <button
                      type="button"
                      onClick={() => setCourseSearch('')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600"
                      aria-label="Clear search"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
                <div className="border border-slate-200 rounded-lg p-3 max-h-40 overflow-y-auto space-y-2">
                  {courses.length === 0 ? (
                    <p className="text-slate-500 text-sm">No courses available</p>
                  ) : (() => {
                    const q = courseSearch.trim().toLowerCase();
                    const visible = q
                      ? courses.filter(c => c.name.toLowerCase().includes(q))
                      : courses;
                    if (visible.length === 0) {
                      return <p className="text-slate-400 text-sm italic">No course matches &ldquo;{courseSearch}&rdquo;</p>;
                    }
                    return visible.map((c) => (
                      <label key={c.id} className="flex items-center gap-2 cursor-pointer hover:bg-slate-50 p-1 rounded">
                        <input
                          type="checkbox"
                          checked={formData.courses.includes(c.name)}
                          onChange={() => handleCourseToggle(c.name)}
                          className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500"
                        />
                        <span className="text-sm text-slate-700">{c.name}</span>
                      </label>
                    ));
                  })()}
                </div>
                {formData.courses.length > 0 && (
                  <p className="text-xs text-indigo-600 mt-1">
                    Selected: {formData.courses.join(', ')}
                  </p>
                )}
              </div>
              )}

              {/* Teacher */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Teacher *</label>
                <select
                  value={formData.teacher_id}
                  onChange={(e) => setFormData({ ...formData, teacher_id: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  required
                  data-testid="class-teacher-select"
                >
                  <option value="">Select Teacher</option>
                  {teachers.map((teacher) => (
                    <option key={teacher.id} value={teacher.id}>{teacher.name}</option>
                  ))}
                </select>
              </div>

              {/* YouTube Channel - Only for YouTube classes */}
              {isYouTube && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    <Youtube size={16} className="inline mr-1 text-red-600" />
                    YouTube Channel
                  </label>
                  <select
                    value={formData.youtube_channel_id}
                    onChange={(e) => setFormData({ ...formData, youtube_channel_id: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                    data-testid="class-channel-select"
                  >
                    <option value="">Select Channel</option>
                    {youtubeChannels.map((channel) => (
                      <option key={channel.id} value={channel.id}>{channel.name}</option>
                    ))}
                  </select>
                  {youtubeChannels.length === 0 && (
                    <p className="text-xs text-slate-500 mt-1">No channels added yet. Click "Channels" button to add.</p>
                  )}
                </div>
              )}

              {isYouTube && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    <ExternalLink size={16} className="inline mr-1 text-blue-600" />
                    Event Link (YouTube/App URL)
                  </label>
                  <input
                    type="url"
                    value={formData.event_link}
                    onChange={(e) => setFormData({ ...formData, event_link: e.target.value })}
                    placeholder="https://youtube.com/watch?v=..."
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                    data-testid="class-event-link"
                  />
                </div>
              )}

              {/* Copy from Last Class - only for new class */}
              {!selectedClass && formData.teacher_id && (isYouTube || formData.courses.length > 0) && (
                <button
                  type="button"
                  onClick={copyFromLastClass}
                  className="w-full py-2 px-4 border-2 border-dashed border-indigo-300 rounded-lg text-indigo-600 hover:bg-indigo-50 hover:border-indigo-400 transition-colors flex items-center justify-center gap-2"
                >
                  <FileText size={18} />
                  Copy from Last Class {isYouTube ? '(Same Teacher)' : '(Same Teacher & Course)'}
                </button>
              )}

              {/* Date & Duration */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Date & Time *
                    {selectedClass && (
                      <span className="text-xs text-amber-600 ml-1">(Change only if needed)</span>
                    )}
                  </label>
                  <input
                    type="datetime-local"
                    value={formData.scheduled_date}
                    onChange={(e) => setFormData({ ...formData, scheduled_date: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    required
                    data-testid="class-datetime-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Duration (mins) *</label>
                  <input
                    type="number"
                    value={formData.duration_minutes}
                    onChange={(e) => setFormData({ ...formData, duration_minutes: parseInt(e.target.value) })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    min="15"
                    required
                    data-testid="class-duration-input"
                  />
                </div>
              </div>

              {/* PDF and DPP Attachments */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <input
                    type="checkbox"
                    id="pdf_attached"
                    checked={formData.pdf_attached}
                    onChange={(e) => setFormData({ ...formData, pdf_attached: e.target.checked })}
                    className="w-5 h-5 text-indigo-600 rounded focus:ring-indigo-500"
                    data-testid="class-pdf-checkbox"
                  />
                  <label htmlFor="pdf_attached" className="text-sm font-medium text-slate-700 cursor-pointer flex items-center gap-1">
                    <FileText size={16} />
                    PDF Attached
                  </label>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg space-y-2">
                  <div className="text-sm font-medium text-slate-700 flex items-center gap-1">
                    <BookOpen size={16} /> DPP Status
                  </div>
                  <div className="flex flex-wrap gap-1.5" data-testid="class-dpp-status">
                    {[
                      { key: 'attached', label: 'Attached', cls: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
                      { key: 'pending', label: 'Required (Pending)', cls: 'bg-orange-100 text-orange-700 border-orange-300' },
                      { key: 'not_required', label: 'Not Required', cls: 'bg-slate-100 text-slate-600 border-slate-300' },
                    ].map(opt => {
                      const currentKey = formData.dpp_required === false
                        ? 'not_required'
                        : (formData.dpp_attached ? 'attached' : 'pending');
                      const selected = currentKey === opt.key;
                      return (
                        <button
                          type="button"
                          key={opt.key}
                          data-testid={`class-dpp-status-${opt.key}`}
                          onClick={() => setFormData({
                            ...formData,
                            dpp_required: opt.key !== 'not_required',
                            dpp_attached: opt.key === 'attached',
                          })}
                          className={`px-2.5 py-1 text-xs font-semibold rounded-md border transition ${
                            selected ? opt.cls + ' ring-2 ring-offset-1 ring-indigo-400' : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300'
                          }`}
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={`px-4 py-2 text-white rounded-lg ${selectedClass ? 'bg-amber-600 hover:bg-amber-700' : 'bg-indigo-600 hover:bg-indigo-700'}`}
                  data-testid="save-class-btn"
                >
                  {selectedClass ? 'Update Class' : 'Create Class'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Import from Date Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-slate-900">Import Classes</h2>
              <button onClick={() => { setShowImportModal(false); setImportDate(''); }} className="text-slate-400 hover:text-slate-600">
                <X size={24} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="p-3 bg-indigo-50 rounded-lg text-sm text-indigo-800">
                <p className="font-medium">Import to: {new Date(selectedDate).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Select date to import from *</label>
                <select
                  value={importDate}
                  onChange={(e) => setImportDate(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">Choose a date</option>
                  {datesWithClasses.filter(d => d !== selectedDate).slice(0, 30).map(date => {
                    const classCount = classes.filter(c => toLocalDateStr(c.scheduled_date) === date).length;
                    return (
                      <option key={date} value={date}>
                        {new Date(date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })} ({classCount} classes)
                      </option>
                    );
                  })}
                </select>
              </div>

              {importDate && (
                <div className="p-3 bg-amber-50 rounded-lg text-sm text-amber-800">
                  <p>This will create copies of all {classes.filter(c => toLocalDateStr(c.scheduled_date) === importDate).length} classes from {new Date(importDate).toLocaleDateString()} with the same times but on {new Date(selectedDate).toLocaleDateString()}.</p>
                  <p className="mt-1 font-medium">PDF/DPP status will be reset to "No".</p>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => { setShowImportModal(false); setImportDate(''); }}
                  className="px-4 py-2 border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleImportFromDate}
                  disabled={!importDate || importingClasses}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {importingClasses ? (
                    <>
                      <Loader2 className="animate-spin" size={18} />
                      Importing...
                    </>
                  ) : (
                    <>
                      <Copy size={18} />
                      Import Classes
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Status Update Modal */}
      {showStatusModal && selectedClass && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-slate-900">Update Event Status</h2>
              <button onClick={() => setShowStatusModal(false)} className="text-slate-400 hover:text-slate-600">
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleStatusUpdate} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Event Status *</label>
                <select
                  value={statusData.status}
                  onChange={(e) => setStatusData({ ...statusData, status: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  required
                  data-testid="status-select"
                >
                  <option value="Scheduled">Scheduled</option>
                  <option value="Run as per Plan">Run as per Plan</option>
                  <option value="Cancelled">Cancelled</option>
                  <option value="Proxy Teacher Used">Proxy Teacher Used</option>
                  <option value="Old Recording">Old Recording</option>
                  <option value="New Recording">New Recording</option>
                </select>
              </div>
              {(statusData.status === 'Run as per Plan' || statusData.status === 'New Recording') && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Actual Duration (mins)</label>
                  <input
                    type="number"
                    value={statusData.actual_duration_minutes}
                    onChange={(e) => setStatusData({ ...statusData, actual_duration_minutes: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    min="1"
                    data-testid="actual-duration-input"
                  />
                </div>
              )}
              {statusData.status === 'Cancelled' && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Cancellation Reason</label>
                  <textarea
                    value={statusData.cancellation_reason}
                    onChange={(e) => setStatusData({ ...statusData, cancellation_reason: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    rows="3"
                    data-testid="cancellation-reason-input"
                  />
                </div>
              )}
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowStatusModal(false)}
                  className="px-4 py-2 border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                  data-testid="update-status-btn"
                >
                  Update Status
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Substitute/Proxy Teacher Modal */}
      {showSubstituteModal && selectedClass && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-slate-900">Assign Proxy Teacher</h2>
              <button onClick={() => setShowSubstituteModal(false)} className="text-slate-400 hover:text-slate-600">
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleSubstituteAssign} className="p-6 space-y-4">
              <div className="p-3 bg-amber-50 rounded-lg text-sm text-amber-800">
                <p className="font-medium">Class: {selectedClass.title}</p>
                <p>Original Teacher: {selectedClass.teacher_name}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Select Proxy Teacher *</label>
                <select
                  value={substituteId}
                  onChange={(e) => setSubstituteId(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  required
                  data-testid="substitute-select"
                >
                  <option value="">Select Teacher</option>
                  {teachers.filter(t => t.id !== selectedClass.teacher_id).map((teacher) => (
                    <option key={teacher.id} value={teacher.id}>{teacher.name}</option>
                  ))}
                </select>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowSubstituteModal(false)}
                  className="px-4 py-2 border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600"
                  data-testid="assign-proxy-btn"
                >
                  Assign Proxy
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Subject Modal */}
      {showAddSubjectModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-sm max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200">
              <h2 className="text-xl font-semibold text-slate-900">Add Subject</h2>
            </div>
            <form onSubmit={handleAddSubject} className="p-6 space-y-4">
              <input
                type="text"
                value={newSubject}
                onChange={(e) => setNewSubject(e.target.value)}
                placeholder="Subject Name"
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                required
              />
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowAddSubjectModal(false)}
                  className="px-4 py-2 border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                >
                  Add Subject
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Course Modal */}
      {showAddCourseModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-sm max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200">
              <h2 className="text-xl font-semibold text-slate-900">Add Course</h2>
            </div>
            <form onSubmit={handleAddCourse} className="p-6 space-y-4">
              <input
                type="text"
                value={newCourse}
                onChange={(e) => setNewCourse(e.target.value)}
                placeholder="Course Name"
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                required
              />
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowAddCourseModal(false)}
                  className="px-4 py-2 border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                >
                  Add Course
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* YouTube Channel Management Modal */}
      {showChannelModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-hidden">
            <div className="flex justify-between items-center px-6 py-4 border-b">
              <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
                <Youtube className="text-red-600" size={24} />
                Manage YouTube Channels
              </h2>
              <button onClick={() => { setShowChannelModal(false); setNewChannelName(''); setEditingChannel(null); }} className="p-1 hover:bg-slate-100 rounded">
                <X size={24} />
              </button>
            </div>
            
            <div className="p-6">
              {/* Add/Edit Channel Form */}
              <form onSubmit={handleAddChannel} className="flex gap-2 mb-4">
                <input
                  type="text"
                  value={newChannelName}
                  onChange={(e) => setNewChannelName(e.target.value)}
                  placeholder="Channel name"
                  className="flex-1 px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                  data-testid="channel-name-input"
                />
                <button
                  type="submit"
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
                  data-testid="add-channel-btn"
                >
                  {editingChannel ? 'Update' : 'Add'}
                </button>
                {editingChannel && (
                  <button
                    type="button"
                    onClick={() => { setEditingChannel(null); setNewChannelName(''); }}
                    className="px-3 py-2 border border-slate-200 rounded-lg hover:bg-slate-50"
                  >
                    Cancel
                  </button>
                )}
              </form>
              
              {/* Channel List */}
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {youtubeChannels.length === 0 ? (
                  <p className="text-center text-slate-500 py-4">No channels added yet</p>
                ) : (
                  youtubeChannels.map((channel) => (
                    <div 
                      key={channel.id}
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                      data-testid={`channel-item-${channel.id}`}
                    >
                      <span className="font-medium text-slate-700">{channel.name}</span>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => { setEditingChannel(channel); setNewChannelName(channel.name); }}
                          className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                          title="Edit"
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          onClick={() => handleDeleteChannel(channel)}
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Classes;
